import React, { createRef, useState, useEffect, useCallback } from 'react';

import Tooltip from 'generics/components/ht-tooltip';

import { compareTwoValuesEquality, positionSingleSelectOptions } from './utilities';

import useOnClickOutside from 'generics/hooks/on-click-outside';

import './index.scss';

function SingleSelect(props) {

  const {
    options,
    value,
    onChange,
    placeholder,
    mainClass,
    singleSelectButtonClass,
    singleSelectOptionWrapperClass,
    singleSelectOptionClass,
    determineSelectOptionClass,
    labelFieldName,
    valueFieldName,
    disabled
  } = props;

  const _singleSelectButtonClass = singleSelectButtonClass || '';

  const [displaySingleSelectOptions, setDisplaySingleSelectOptions] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);

  const singleSelectWrapperRef = createRef(null);
  const singleSelectOptionListReference = createRef(null);
  const selectButtonReference = createRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const determineSelectOptionClassFunction = useCallback(determineSelectOptionClass || (() => ''), [determineSelectOptionClass]);

  useOnClickOutside(singleSelectWrapperRef, closeSingleSelectOptions);

  const dropdownOptions = createDataSetFromActivePageList();

  useEffect(function onValueChange() {

    setSelectedValue(value);

  }, [value]);

  useEffect(function onSingleSelectOptionListReferenceChange() {

    tryPositionSingleSelectOptionList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleSelectOptionListReference]);

  useEffect(function onLoad() {

    tryAddTooltip();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function tryAddTooltip() {

    const tooltipSettings = props.tooltip || { display: false };

    if (tooltipSettings.display === false) {
      return;
    }

    new Tooltip({
      target: selectButtonReference.current,
      content: tooltipSettings.text
    });

  }

  function tryPositionSingleSelectOptionList() {
    if (singleSelectOptionListReference.current === null) {
      return;
    }

    positionSingleSelectOptions(singleSelectOptionListReference.current, selectButtonReference.current, props.optionsWidth);
  }

  function handleDisplaySingleSelectOptions() {
    setDisplaySingleSelectOptions(function updateDisplaySingleSelectionOptions(_displaySingleSelectOptions) {
      return !_displaySingleSelectOptions;
    });
  }

  function closeSingleSelectOptions() {
    setDisplaySingleSelectOptions(false);
  }

  function handleSelectOptionClick(optionValue) {

    closeSingleSelectOptions();

    if (optionValue.disabled === true) {
      return;
    }

    setSelectedValue(optionValue.value);
    onChange(optionValue);

  }

  function createDataSetFromActivePageList() {
    const updatedData = [];
    const optionsCount = options.length;

    for (let index = 0; index < optionsCount; index++) {
      const option = options[index];
      const object = {
        ...option,
        label: option[labelFieldName],
        value: option[valueFieldName],
        disabled: option.disabled === true
      };
      updatedData.push(object);
    }

    return updatedData;

  }

  function getSelectedOption() {
    const dropdownOptionsLength = dropdownOptions.length;
    let selectedOption = null;

    for (let index = 0; index < dropdownOptionsLength; index++) {
      if (selectedValue === dropdownOptions[index].value) {
        selectedOption = dropdownOptions[index];
      }
    }

    return selectedOption;
  }

  function renderSingleSelectArrows() {

    const windUnwindIndicatorAttributes = {
      className: 'wind-unwind-indicator'
    };

    if (displaySingleSelectOptions === false) {
      windUnwindIndicatorAttributes.className += ' unwind';
    } else {
      windUnwindIndicatorAttributes.className += ' wind';
    }

    return <span {...windUnwindIndicatorAttributes} />;

  }

  function renderSingleSelectOptionContent(optionValue, index) {

    if (typeof props.renderOption === 'function') {
      return props.renderOption(optionValue, index);
    }

    return optionValue.label;

  }

  function renderSingleSelectOption(optionValue, index) {

    let selectedOptionClassName = '';
    let disabledClassName = '';
    const selectedOption = getSelectedOption();

    if (compareTwoValuesEquality(optionValue, selectedOption) === true) {
      selectedOptionClassName = 'active-select-option';
    }

    if (optionValue.disabled === true) {
      disabledClassName = 'disabled';
    }

    const selectOptionAttributes = {
      className: `single-select-option ${selectedOptionClassName} ${singleSelectOptionClass} ${disabledClassName} ${determineSelectOptionClassFunction(optionValue)}`,
      value: optionValue.value,
      onClick(event) {
        event.preventDefault();
        event.stopPropagation();
        handleSelectOptionClick(optionValue);
        return true;
      }
    };

    return <li key={index} {...selectOptionAttributes}>
      {renderSingleSelectOptionContent(optionValue, index)}
    </li>;
  }

  function renderSingleSelectOptions() {

    if (displaySingleSelectOptions === false) {
      return;
    }

    if (disabled === true) {
      return;
    }

    const optionsLength = dropdownOptions.length;
    const elements = [];

    for (let index = 0; index < optionsLength; index++) {
      const selectOptionValue = dropdownOptions[index];
      const element = renderSingleSelectOption(selectOptionValue, index);

      elements.push(element);
    }

    const singleSelectOptionListAttributes = {
      className: `single-select-options-list ${singleSelectOptionWrapperClass} hide-scrollbar`,
      ref: singleSelectOptionListReference,
      style: {
        // width: getSelectedOptionsListWidth()
      }
    };

    return <ul {...singleSelectOptionListAttributes}>{elements}</ul>;
  }

  function renderSingleSelectLabel() {

    const labelAttributes = {
      className: 'single-select-label ellipsis'
    };

    let selectedOption = getSelectedOption();

    if (selectedOption === null) {
      return <label {...labelAttributes}>{placeholder}</label>;
    } else {
      return <label {...labelAttributes}>{renderSingleSelectOptionContent(selectedOption)}</label>;
    }
  }

  function renderMain() {

    let selectButtonAttributes = {
      className: `single-select-label-button ${_singleSelectButtonClass}`,
      disabled,
      onClick(event) {
        event.preventDefault();
        event.stopPropagation();
        handleDisplaySingleSelectOptions();
        return false;
      },
      ref: selectButtonReference,
    };

    if (disabled === true) {
      selectButtonAttributes.className += ' disabled';
    }

    const singleSelectWrapperAttributes = {
      className: `single-select-wrapper ${mainClass || ''}`,
      ref: singleSelectWrapperRef
    };

    return <div {...singleSelectWrapperAttributes}>

      <button {...selectButtonAttributes}>
        {renderSingleSelectLabel()}
        {renderSingleSelectArrows()}
      </button>

      {renderSingleSelectOptions()}

    </div>;
  }

  return renderMain();
}

export default SingleSelect;